import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';

export interface RotationalScheduleValue {
  weeks: string[][];
}

export interface RotationalScheduleProps
  extends Omit<Partial<FieldRenderProps>, 'onChange' | 'value'> {
  onChange: (e: { value: RotationalScheduleValue }) => void;
  value?: RotationalScheduleValue;
  processedProps?: boolean;
}

const MIN_WEEKS = 2;
const MAX_WEEKS = 6;
const MAX_HOURS = 168; // 7 × 24
const MAX_ERROR_TEXT = `Max value: ${MAX_HOURS} hrs.`;

const INPUT_BASE =
  'tw-w-[70px] tw-h-8 tw-text-center tw-text-base tw-font-semibold ' +
  'tw-bg-unm-bg-light tw-border tw-rounded-lg tw-outline-none';
const INPUT_ENABLED =
  'tw-border-unm-border1 focus:tw-border-unm-interactive01-400 tw-cursor-text';
const INPUT_ERROR =
  'tw-border-2 tw-border-[#B42318] focus:tw-border-[#B42318] tw-cursor-text';

const inputClass = (error: boolean) =>
  `${INPUT_BASE} ${INPUT_ENABLED} ${error ? INPUT_ERROR : ''}`;

const isOver = (v: string) =>
  v !== '' &&
  !Number.isNaN(Number.parseInt(v)) &&
  Number.parseInt(v) > MAX_HOURS;

const ADD_BTN =
  'tw-inline-flex tw-items-center tw-px-4 tw-py-1.5 tw-rounded-full ' +
  'tw-text-base tw-font-semibold tw-border tw-border-solid ' +
  'tw-bg-white tw-text-unm-adhoc2 tw-border-gray tw-cursor-pointer ' +
  'hover:tw-bg-unm-bg-light';

export function RotationalSchedule({
  onChange,
  value,
  onBlur,
  processedProps,
}: Readonly<RotationalScheduleProps>) {
  const nextId = React.useRef(0);
  const makeId = () => nextId.current++;

  const initWeeks = () => {
    const existing = value?.weeks;
    const hrs =
      existing && existing.length >= MIN_WEEKS
        ? existing
        : Array.from({ length: MIN_WEEKS }, () => ['']);
    return hrs.map((h) => ({ id: makeId(), hours: h[0] ?? '' }));
  };

  const [weeks, setWeeks] = React.useState(initWeeks);
  const [validationError, setValidationError] = React.useState('');

  React.useEffect(() => {
    if (!processedProps) return;
    const existing = value?.weeks;
    const hrs =
      existing && existing.length >= MIN_WEEKS
        ? existing
        : Array.from({ length: MIN_WEEKS }, () => ['']);
    setWeeks(hrs.map((h) => ({ id: makeId(), hours: h[0] ?? '' })));
    setValidationError('');
    onChange({ value: { weeks: hrs } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedProps]);

  const onWeekHoursChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (
        v !== '' &&
        !Number.isNaN(Number.parseInt(v)) &&
        Number.parseInt(v) > MAX_HOURS
      ) {
        setValidationError(MAX_ERROR_TEXT);
      } else {
        setValidationError('');
      }
      setWeeks((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], hours: v };
        onChange({ value: { weeks: next.map((w) => [w.hours]) } });
        return next;
      });
    };

  const addWeek = () => {
    if (weeks.length >= MAX_WEEKS) return;
    setWeeks((prev) => {
      const next = [...prev, { id: makeId(), hours: '' }];
      onChange({ value: { weeks: next.map((w) => [w.hours]) } });
      return next;
    });
  };

  const removeWeek = (index: number) => {
    if (weeks.length <= MIN_WEEKS) return;
    setWeeks((prev) => {
      const next = prev.filter((_, i) => i !== index);
      onChange({ value: { weeks: next.map((w) => [w.hours]) } });
      return next;
    });
  };

  const handleBlur = (e: React.FocusEvent<HTMLFieldSetElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      onBlur?.();
    }
  };

  return (
    <fieldset
      id="form-variable-rotational-schedule"
      className="tw-w-full tw-border-0 tw-p-0 tw-m-0"
      onBlur={handleBlur}
    >
      <div className="tw-bg-white tw-shadow-[0px_2px_4px_0px_rgba(2,84,126,0.20)] tw-rounded-[8px] tw-px-5 tw-py-8 tw-flex tw-flex-col tw-gap-5">
        <p className="tw-text-base tw-text-unm-adhoc2">
          Enter the hours for each week in your rotating cycle. Days vary - we
          just need your total hours per week.
        </p>

        {/* Week rows */}
        <div className="tw-flex tw-flex-col tw-gap-1">
          {weeks.map((week, i) => (
            <div
              key={week.id}
              className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-2"
            >
              <span className="tw-w-[90px] tw-shrink-0 tw-text-base tw-font-semibold tw-text-unm-adhoc2">
                Week {i + 1}
              </span>
              <input
                type="number"
                id={`form-variable-rotational-week-${i}`}
                min={0}
                max={MAX_HOURS}
                value={week.hours}
                placeholder="0"
                onChange={onWeekHoursChange(i)}
                className={inputClass(isOver(week.hours))}
                aria-label={`Week ${i + 1} hours`}
              />
              <span className="tw-text-base tw-text-unm-adhoc2">hrs</span>
              {i >= MIN_WEEKS && (
                <button
                  type="button"
                  onClick={() => removeWeek(i)}
                  className="tw-ml-auto tw-text-base tw-text-unm-interactive01-400 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-underline tw-underline"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Add week button */}
        {weeks.length < MAX_WEEKS && (
          <div className="tw-pl-5">
            <button type="button" onClick={addWeek} className={ADD_BTN}>
              + Add week
            </button>
          </div>
        )}

        {validationError && (
          <p
            id="form-variable-rotational-schedule-error"
            className="tw-text-sm tw-font-semibold tw-text-[#B42318]"
          >
            {validationError}
          </p>
        )}
      </div>
    </fieldset>
  );
}
